import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Printer, Send, Loader2, CheckCircle2, Building2, Pencil, Plus, Trash2, X, Save } from "lucide-react";

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [validating, setValidating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/invoices/${id}`);
    setInv(data);
  };
  useEffect(() => { load(); }, [id]);

  const validate = async () => {
    setValidating(true);
    try {
      const { data } = await api.post(`/invoices/${id}/validate`);
      setInv((p) => ({ ...p, status: "sent", bexio_synced: true }));
      toast.success("Facture validée", { description: data.message });
    } catch (err) {
      toast.error("Échec de la validation", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setValidating(false);
    }
  };

  const startEdit = () => {
    setForm({
      client_name: inv.client_name || "",
      company: inv.company || "",
      client_email: inv.client_email || "",
      date: inv.date || "",
      items: (inv.items || []).map((i) => ({ ...i })),
      tva_rate: inv.tva_rate ?? 8.1,
    });
    setEditing(true);
  };

  const updateItem = (idx, field, value) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: field === "description" ? value : parseFloat(value) || 0 };
      return { ...f, items };
    });
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { description: "", quantity: 1, unit_price: 0 }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const saveEdit = async () => {
    if (!form.client_name.trim()) return toast.error("Le nom du client est requis");
    const items = form.items.filter((i) => i.description.trim());
    if (items.length === 0) return toast.error("Ajoutez au moins un article");
    setSaving(true);
    try {
      await api.put(`/invoices/${id}`, {
        client_name: form.client_name,
        company: form.company,
        client_email: form.client_email,
        date: form.date,
        items,
        tva_rate: form.tva_rate,
      });
      toast.success("Facture mise à jour");
      setEditing(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!inv) {
    return <div className="p-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#5E2129]" /></div>;
  }

  const isSent = inv.status === "sent";
  const editGross = editing ? form.items.reduce((s, i) => s + i.quantity * i.unit_price, 0) : 0;
  const editTva = editGross - editGross / (1 + (form?.tva_rate ?? 8.1) / 100);
  const editSubtotal = editGross - editTva;
  const editTotal = editGross;

  return (
    <div className="min-h-screen bg-[#F1EADE]/40">
      {/* Action bar */}
      <div className="no-print sticky top-0 z-10 backdrop-blur bg-[#FDFBF7]/85 border-b border-[#EBE5D9] px-4 md:px-12 py-4 flex items-center justify-between gap-2">
        <button onClick={() => navigate("/factures")} className="flex items-center gap-2 text-sm text-[#666] hover:text-[#5E2129] transition-colors shrink-0">
          <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Factures</span>
        </button>
        <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-end">
          {editing ? (
            <>
              <Button variant="outline" data-testid="cancel-edit-btn" onClick={() => setEditing(false)} className="rounded-full border-[#5E2129]/30 text-[#5E2129] hover:bg-[#5E2129]/5">
                <X className="w-4 h-4 mr-2" /> Annuler
              </Button>
              <Button data-testid="save-edit-btn" onClick={saveEdit} disabled={saving} className="rounded-full bg-[#5E2129] hover:bg-[#4A1A20] text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Enregistrer</>}
              </Button>
            </>
          ) : (
            <>
              {!isSent && (
                <Button variant="outline" data-testid="edit-invoice-btn" onClick={startEdit} className="rounded-full border-[#5E2129]/30 text-[#5E2129] hover:bg-[#5E2129]/5">
                  <Pencil className="w-4 h-4 mr-2" /> Modifier
                </Button>
              )}
              <Button variant="outline" data-testid="print-btn" onClick={() => window.print()} className="rounded-full border-[#5E2129]/30 text-[#5E2129] hover:bg-[#5E2129]/5">
                <Printer className="w-4 h-4 mr-2" /> Imprimer
              </Button>
              {isSent ? (
                <span data-testid="sent-badge" className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full bg-[#2E4F36]/10 text-[#2E4F36]">
                  <CheckCircle2 className="w-4 h-4" /> Envoyée
                </span>
              ) : !inv.bexio_contact_id ? (
                <span data-testid="pending-client-note" className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full bg-[#D97736]/10 text-[#D97736]">
                  <Building2 className="w-4 h-4" /> Client à créer dans Bexio
                </span>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid="validate-invoice-btn" className="rounded-full bg-[#5E2129] hover:bg-[#4A1A20] text-white">
                      <Send className="w-4 h-4 mr-2" /> Valider & Envoyer
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-white" data-testid="validate-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-serif text-2xl">Confirmer l'envoi ?</AlertDialogTitle>
                      <AlertDialogDescription className="text-[#666]">
                        La facture <strong>{inv.invoice_number}</strong> ({inv.client_name}) d'un montant de{" "}
                        <strong>CHF {inv.total?.toFixed(2)}</strong> sera envoyée par email au client et
                        enregistrée dans votre comptabilité Bexio.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="cancel-validate">Annuler</AlertDialogCancel>
                      <AlertDialogAction data-testid="confirm-validate" onClick={validate} disabled={validating} className="bg-[#5E2129] hover:bg-[#4A1A20]">
                        {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer & Envoyer"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </>
          )}
        </div>
      </div>

      {!editing && inv.client_status === "pending" && (
        <div className="no-print mx-4 md:mx-12 mt-6 flex items-start gap-3 bg-[#D97736]/10 text-[#D97736] rounded-lg px-4 py-3">
          <Building2 className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="text-sm">Ce client est <strong>en attente</strong> dans Bexio. Créez-le depuis la page Clients (bouton « Créer dans Bexio »).</p>
        </div>
      )}

      {editing ? (
        /* ---------- Edit form ---------- */
        <div className="py-8 px-4 flex justify-center">
          <div className="w-full max-w-[820px] bg-white border border-[#EBE5D9] rounded-xl p-6 md:p-10 space-y-6" data-testid="invoice-edit-form">
            <h2 className="font-serif text-2xl text-[#1A1A1A]">Modifier la facture {inv.invoice_number}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5E2129] font-semibold">Nom du client</Label>
                <Input data-testid="edit-client-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="mt-2 bg-[#FDFBF7] border-[#EBE5D9]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5E2129] font-semibold">Entreprise</Label>
                <Input data-testid="edit-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-2 bg-[#FDFBF7] border-[#EBE5D9]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5E2129] font-semibold">Email</Label>
                <Input data-testid="edit-email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="mt-2 bg-[#FDFBF7] border-[#EBE5D9]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5E2129] font-semibold">Date</Label>
                <Input data-testid="edit-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} placeholder="JJ.MM.AAAA" className="mt-2 bg-[#FDFBF7] border-[#EBE5D9]" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-[#5E2129] font-semibold">Lignes de la facture</Label>
                <button onClick={addItem} data-testid="edit-add-item" className="text-sm text-[#5E2129] font-semibold flex items-center gap-1 hover:gap-2 transition-all">
                  <Plus className="w-4 h-4" /> Ajouter
                </button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, idx) => (
                  <div key={idx} className="flex gap-2 items-center" data-testid={`edit-item-row-${idx}`}>
                    <Input value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} placeholder="Article" className="flex-1 bg-[#FDFBF7] border-[#EBE5D9]" />
                    <Input type="number" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="w-16 bg-[#FDFBF7] border-[#EBE5D9]" />
                    <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} className="w-24 bg-[#FDFBF7] border-[#EBE5D9]" />
                    <button onClick={() => removeItem(idx)} className="text-[#666] hover:text-red-600 transition-colors p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-[#EBE5D9] pt-4 space-y-1 text-sm">
              <div className="flex justify-between text-[#666]"><span>Sous-total (HT)</span><span>CHF {editSubtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-[#666]"><span>TVA {form.tva_rate}% incluse</span><span>CHF {editTva.toFixed(2)}</span></div>
              <div className="flex justify-between font-serif text-xl text-[#1A1A1A] pt-1"><span>Total (TTC)</span><span>CHF {editTotal.toFixed(2)}</span></div>
            </div>
          </div>
        </div>
      ) : (
        /* ---------- Paper preview ---------- */
        <div className="py-8 md:py-12 px-4 flex justify-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="invoice-paper print-area w-full max-w-[820px] p-6 sm:p-10 md:p-16"
            data-testid="invoice-paper"
          >
            <div className="flex flex-col sm:flex-row justify-between items-start gap-6 mb-12">
              <div>
                <img src="https://dupeyrou.ch/wp-content/uploads/2026/02/HRDP_logo_V1.png" alt="Restaurant de l'Hôtel DuPeyrou" className="h-16 w-auto mb-3" />
                <p className="text-sm text-[#666] mt-1 leading-relaxed">
                  Restaurant de l'Hôtel DuPeyrou<br />Avenue DuPeyrou 1, 2000 Neuchâtel<br />+41 32 725 11 83 · info@dupeyrou.ch
                </p>
              </div>
              <div className="sm:text-right">
                <p className="font-serif text-4xl text-[#1A1A1A]">FACTURE</p>
                <p className="text-sm text-[#666] mt-2">{inv.invoice_number}</p>
                <p className="text-sm text-[#666]">{inv.date}</p>
              </div>
            </div>

            <div className="mb-10">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5E2129] mb-2">Facturé à</p>
              <p className="font-semibold text-[#1A1A1A]">{inv.client_name}</p>
              {inv.company && inv.company !== inv.client_name && <p className="text-sm text-[#666]">{inv.company}</p>}
              {inv.client_email && <p className="text-sm text-[#666]">{inv.client_email}</p>}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full mb-10 min-w-[400px]">
                <thead>
                  <tr className="border-b-2 border-[#1A1A1A] text-left text-xs uppercase tracking-wider text-[#666]">
                    <th className="py-3">Désignation</th>
                    <th className="py-3 text-center w-20">Qté</th>
                    <th className="py-3 text-right w-28">Prix TTC</th>
                    <th className="py-3 text-right w-28">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.items?.map((it, i) => (
                    <tr key={i} className="border-b border-[#EBE5D9]">
                      <td className="py-3 text-[#1A1A1A]">{it.description}</td>
                      <td className="py-3 text-center text-[#666]">{it.quantity}</td>
                      <td className="py-3 text-right text-[#666]">{it.unit_price.toFixed(2)}</td>
                      <td className="py-3 text-right text-[#1A1A1A]">{(it.quantity * it.unit_price).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end">
              <div className="w-full sm:w-64 space-y-2 text-sm">
                <div className="flex justify-between text-[#666]"><span>Sous-total (HT)</span><span>CHF {inv.subtotal?.toFixed(2)}</span></div>
                <div className="flex justify-between text-[#666]"><span>TVA {inv.tva_rate}% incluse</span><span>CHF {inv.tva_amount?.toFixed(2)}</span></div>
                <div className="flex justify-between font-serif text-2xl text-[#5E2129] pt-3 border-t-2 border-[#1A1A1A]">
                  <span>Total (TTC)</span><span>CHF {inv.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="mt-16 pt-6 border-t border-[#EBE5D9] text-center text-xs text-[#666]">
              <p>Merci de votre visite — Restaurant de l'Hôtel DuPeyrou, Neuchâtel</p>
              <p className="mt-1">Avenue DuPeyrou 1, 2000 Neuchâtel · +41 32 725 11 83 · info@dupeyrou.ch</p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
